// src/components/settings/ProviderSettings.jsx
// Ganti SettingsPanel, tanpa input API Key (server-side only)

import React from 'react';
import { Server, Zap, Check, ShieldCheck, Activity, Globe } from 'lucide-react';

const PROVIDERS = [
  {
    id:            'gemini',
    name:          'Google Gemini',
    tagline:       'Gratis & Cepat',
    model:         'gemini-2.5-flash',
    description:   'Cepat dan gratis, cocok untuk analisis sehari-hari.',
    activeBg:      'bg-sky-50 dark:bg-sky-900/20',
    activeBorder:  'border-sky-400 dark:border-sky-600',
    activeText:    'text-sky-800 dark:text-sky-300',
    activeTag:     'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-400',
    checkColor:    'text-sky-500',
    inactiveBg:    'bg-stone-50 dark:bg-slate-700',
    inactiveBorder:'border-stone-200 dark:border-slate-600',
    inactiveText:  'text-slate-600 dark:text-slate-300',
  },
  {
    id:            'cohere',
    name:          'Cohere AI',
    tagline:       'Presisi Tinggi',
    model:         'command-r-08-2024',
    description:   'Presisi lebih tinggi untuk teks Arab kompleks.',
    activeBg:      'bg-purple-50 dark:bg-purple-900/20',
    activeBorder:  'border-purple-400 dark:border-purple-600',
    activeText:    'text-purple-800 dark:text-purple-300',
    activeTag:     'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-400',
    checkColor:    'text-purple-500',
    inactiveBg:    'bg-stone-50 dark:bg-slate-700',
    inactiveBorder:'border-stone-200 dark:border-slate-600',
    inactiveText:  'text-slate-600 dark:text-slate-300',
  },
];

export default function ProviderSettings({ activeProvider, onProviderChange }) {
  return (
    <div className="space-y-4">

      {/* ── Judul Halaman ────────────────────────────── */}
      <div className="pt-1 pb-1">
        <h2 className="text-base font-extrabold text-slate-800 dark:text-slate-100 flex items-center gap-2">
          <Server className="w-5 h-5 text-green-500" />
          Pengaturan AI Engine
        </h2>
        <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
          Pilih model AI untuk analisis Nahwu
        </p>
      </div>

      {/* ── Info Server-Side ─────────────────────────── */}
      <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800/40 rounded-3xl p-4 flex items-start gap-3">
        <ShieldCheck className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-xs font-extrabold text-green-800 dark:text-green-300">
            API Key Dikelola Server
          </p>
          <p className="text-[11px] text-green-700 dark:text-green-400 mt-0.5 leading-relaxed">
            Anda tidak perlu memasukkan API Key. Semua request diproses aman melalui server Cloudflare.
          </p>
        </div>
      </div>

      {/* ── Pilih Provider ───────────────────────────── */}
      <div className="bg-white dark:bg-slate-800 rounded-3xl shadow-soft border border-stone-100 dark:border-slate-700 p-4 space-y-3">
        <p className="text-[10px] font-extrabold text-stone-500 dark:text-slate-400 uppercase tracking-widest">
          Pilih AI Provider
        </p>

        <div className="space-y-2.5">
          {PROVIDERS.map((p) => {
            const isActive = activeProvider === p.id;
            return (
              <button
                key={p.id}
                onClick={() => onProviderChange(p.id)}
                className={`
                  relative w-full p-4 rounded-3xl border-2
                  flex items-start gap-3
                  transition-all duration-200 active:scale-[0.98]
                  text-left
                  ${isActive
                    ? `${p.activeBg} ${p.activeBorder}`
                    : `${p.inactiveBg} ${p.inactiveBorder} hover:border-stone-300`
                  }
                `}
              >
                {/* Centang Aktif */}
                {isActive && (
                  <div className="absolute top-3.5 right-3.5">
                    <Check className={`w-4 h-4 ${p.checkColor}`} strokeWidth={3} />
                  </div>
                )}

                {/* Ikon */}
                <div className={`w-10 h-10 rounded-2xl flex items-center justify-center flex-shrink-0 ${isActive ? p.activeTag : 'bg-stone-200 dark:bg-slate-600'}`}>
                  <Zap className={`w-5 h-5 ${isActive ? '' : 'text-slate-400'}`} />
                </div>

                {/* Konten */}
                <div className="flex-1 min-w-0 pr-6">
                  <div className="flex items-center gap-1.5 mb-1">
                    <span className={`text-sm font-extrabold leading-tight ${isActive ? p.activeText : p.inactiveText}`}>
                      {p.name}
                    </span>
                    <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${isActive ? p.activeTag : 'bg-stone-200 dark:bg-slate-600 text-stone-500 dark:text-slate-400'}`}>
                      {p.tagline}
                    </span>
                  </div>
                  <p className={`text-[10px] font-mono ${isActive ? 'text-slate-500 dark:text-slate-400' : 'text-stone-400 dark:text-slate-500'}`}>
                    {p.model}
                  </p>
                  <p className={`text-[11px] mt-1 leading-snug ${isActive ? p.activeText : 'text-slate-500 dark:text-slate-400'}`}>
                    {p.description}
                  </p>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Info Aplikasi ────────────────────────────── */}
      <div className="bg-white dark:bg-slate-800 rounded-3xl shadow-soft border border-stone-100 dark:border-slate-700 p-4 space-y-3">
        <p className="text-[10px] font-extrabold text-stone-500 dark:text-slate-400 uppercase tracking-widest">
          Tentang Aplikasi
        </p>

        <div className="space-y-2.5">
          {[
            { icon: Activity, label: 'Versi',    value: '2.0.0' },
            { icon: Globe,    label: 'Platform', value: 'Cloudflare Pages' },
            { icon: Server,   label: 'Runtime',  value: 'Edge Network' },
          ].map((info, i) => {
            const Icon = info.icon;
            return (
              <div key={i} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Icon className="w-3.5 h-3.5 text-stone-400 dark:text-slate-500" />
                  <span className="text-xs text-slate-500 dark:text-slate-400">{info.label}</span>
                </div>
                <span className="text-xs font-bold text-slate-700 dark:text-slate-300 font-mono">
                  {info.value}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Footer ──────────────────────────────────── */}
      <p className="text-center text-[10px] text-slate-400 dark:text-slate-500 font-medium pt-2">
        Dibuat dengan untuk Santri Indonesia
      </p>

    </div>
  );
}